import React, { useEffect, useState } from 'react';
import { List, Radio, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    StatusBadge,
} from "../../components/ui";

const statusTones = {
    new: 'blue',
    queue: 'amber',
    examined: 'green',
};

const formatVisitDate = (text, t) => {
    const date = new Date(text);
    return !isNaN(date)
        ? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium' }).format(date)
        : t('queues.noDate');
};

const formatVisitTime = (text, t) => {
    const time = new Date(text);
    return !isNaN(time)
        ? new Intl.DateTimeFormat('uz-UZ', { timeStyle: 'short' }).format(time)
        : t('queues.notStarted');
};

const VisitTable = () => {
    const { t } = useTranslation();
    const [visitData, setVisitData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    // Fetch visit data
    const fetchVisitData = async () => {
        try {
            const response = await axiosInstance.get('visit', {
                params: {
                    'status[0]': 'new',
                    'status[1]': 'queue',
                    'status[2]': 'examined',
                },
            });
            setVisitData(response.data.data);
        } catch (error) {
            console.error('Error fetching visit data:', error);
            message.error(t('common.loadError'));
        } finally {
            setLoading(false);
        }
    };

    // Fetch patients with visit status
    const fetchPatients = async () => {
        try {
            const response = await axiosInstance.get('/patients');
            const patientsWithVisitStatus = response.data.data.map(patient => ({
                ...patient,
                isExamined: patient.visit_status?.status === 'examined',
            }));
            setPatients(patientsWithVisitStatus);
        } catch (error) {
            console.error("Error fetching patients:", error);
            message.error(t('callCenter.patientsError'));
        }
    };

    // Fetch doctors from users with doctor role
    const fetchDoctors = async () => {
        try {
            const response = await axiosInstance.get('/admin/users');
            const doctorUsers = response.data.data.filter(user => user.roles.includes('doctor'));
            setDoctors(doctorUsers);
        } catch (error) {
            console.error("Error fetching doctors:", error);
            message.error(t('queues.doctorsError'));
        }
    };

    useEffect(() => {
        fetchVisitData();
        fetchPatients();
        fetchDoctors();
    }, []);

    // Handle patient selection
    const handleSelectPatient = (patientId) => {
        setSelectedPatient(patientId);
    };

    // Handle doctor selection
    const handleSelectDoctor = (doctorId) => {
        setSelectedDoctor(doctorId);
    };

    // Handle visit creation
    const handleCreateVisit = () => {
        if (!selectedPatient || !selectedDoctor) {
            message.warning(t('queues.select'));
            return;
        }
        message.success(t('queues.created', { patientId: selectedPatient, doctorId: selectedDoctor }));
        setSelectedPatient(null);
        setSelectedDoctor(null);
    };

    const statusLabel = (status) => {
        const key = `status.${status}`;
        const translated = t(key);
        return translated === key ? status : translated;
    };

    // Define table columns
    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('common.patient'),
            dataIndex: ['patient_id', 'name'],
            key: 'patient_id.name',
            render: (text) => (
                <span className="block text-sm font-medium text-[#1D2C7B] min-w-[150px]">{text}</span>
            ),
        },
        {
            title: t('common.doctor'),
            dataIndex: ['doctor', 'name'],
            key: 'doctor.name',
            render: (text) => <span className="text-sm text-slate-700">{text}</span>,
        },
        {
            title: t('common.date'),
            dataIndex: 'date_at',
            key: 'date_at',
            width: 160,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{formatVisitDate(text, t)}</span>
            ),
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status) => (
                <StatusBadge tone={statusTones[status] ?? 'muted'}>
                    {statusLabel(status)}
                </StatusBadge>
            ),
        },
        {
            title: t('queues.visitTime'),
            dataIndex: 'visit_at',
            key: 'visit_at',
            width: 180,
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{formatVisitTime(text, t)}</span>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <span className="block text-sm font-semibold text-[#1D2C7B] truncate">
                {record.patient_id?.name}
            </span>
            <span className="block text-xs text-slate-500 mt-0.5 truncate">{record.doctor?.name}</span>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <StatusBadge tone={statusTones[record.status] ?? 'muted'}>
                    {statusLabel(record.status)}
                </StatusBadge>
                <span className="tabular-nums">{formatVisitDate(record.date_at, t)}</span>
                <span className="tabular-nums">{formatVisitTime(record.visit_at, t)}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-2.5 md:gap-4 items-start">
                {/* Sidebar for patient and doctor selection */}
                <PageCard className="w-full">
                    <PageToolbar title={t('queues.select')} />

                    <div className="p-2.5 sm:p-3">
                        {/* Patient selection */}
                        <Radio.Group
                            onChange={(e) => handleSelectPatient(e.target.value)}
                            value={selectedPatient}
                            className="w-full mb-4"
                        >
                            <List
                                dataSource={patients}
                                renderItem={(patient) => (
                                    <List.Item className="flex items-center">
                                        <Radio value={patient.id} disabled={patient.isExamined}>
                                            <span className="text-sm text-slate-700">
                                                {patient.name || t('common.unknown')}
                                            </span>
                                        </Radio>
                                    </List.Item>
                                )}
                                bordered
                                className="max-h-72 sm:max-h-96 overflow-y-auto rounded-lg"
                            />
                        </Radio.Group>

                        {/* Doctor selection with "buy" tag */}
                        <Select
                            value={selectedDoctor}
                            onChange={handleSelectDoctor}
                            placeholder={t('calendar.pickDoctor')}
                            className="w-full mb-4"
                        >
                            {doctors.map((doctor) => {
                                const isDoctorInVisit = visitData.some(visit => visit.doctor && visit.doctor.id === doctor.id);
                                return (
                                    <Select.Option key={doctor.id} value={doctor.id}>
                                        {doctor.name || t('common.unknown')}
                                        {isDoctorInVisit && <StatusBadge tone="green" className="ml-2">{t('queues.busy')}</StatusBadge>}
                                    </Select.Option>
                                );
                            })}
                        </Select>

                        {/* Confirm visit button */}
                        <ActionButton
                            collapseOnMobile={false}
                            onClick={handleCreateVisit}
                            block
                            className="!w-full !justify-center mt-4"
                        >
                            {t('queues.confirmVisit')}
                        </ActionButton>
                    </div>
                </PageCard>

                {/* Main table content */}
                <PageCard className="w-full">
                    <DataTable
                        columns={columns}
                        dataSource={visitData}
                        mobileCard={renderMobileCard}
                        loading={loading}
                        rowKey="id"
                        scrollX={1100}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            position: ['bottomCenter'],
                            className: 'px-4 pb-3',
                        }}
                    />
                </PageCard>
            </div>
        </PageShell>
    );
};

export default VisitTable;
